import React, {useEffect, useState} from "react";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {DataTable} from "primereact/datatable";

import {useCreateCategorizedDataset, useGetDefaultDatasetCategory} from "api/api-hooks/datasets-hooks";

import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import {DatasetCategoryDropdown} from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";

import {DATASET_ACCESS_TYPES, DATASET_DATA_TYPES} from "../CreateDatasetDialog";
import {BaseDatasetDTO} from "api/model/dataset/BaseDatasetDTO";
import CopyTextWithButton from "components/basic/copy-text-with-button/CopyTextWithButton";

const CreateMultipleDatasets = ({setCreateDialogVisibility}) => {
    const [defaultCategory, setDefaultCategory] = useState<number | undefined>(undefined);
    const [datasetsToCreate, setDatasetsToCreate] = useState<any[]>([]);
    const [createdDatasets, setCreatedDatasets] = useState<Partial<BaseDatasetDTO[]>>([]);

    const defaultCategoryQuery = useGetDefaultDatasetCategory();

    useEffect(() => {
        if (defaultCategoryQuery.isFetched) {
            setDefaultCategory(defaultCategoryQuery.data?.id);
        }
        if (defaultCategoryQuery.isError) {
            easasAlert("Error fetching category", "", "error");
        }
    }, [defaultCategoryQuery.data]);

    const header = (
        <div className="flex gap-2 justify-content-between">
            <DatasetCategoryDropdown value={defaultCategory} onChange={(e) => setDefaultCategory(e)}/>
            <div className="buttons flex gap-2">
                <Button
                    icon="pi pi-plus"
                    className="p-button-success p-mr-2"
                    onClick={() => addDataset()}
                />
                <Button
                    icon="pi pi-minus"
                    className="p-button-danger"
                    onClick={() => removeDataset()}
                />
            </div>
        </div>
    );

    const addDataset = () => {
        setDatasetsToCreate([
            ...datasetsToCreate,
            {
                id: datasetsToCreate.length + 1,
                title: "",
                categoryId: defaultCategory,
                dataType: "ARBITRARY",
                accessType: "NORMAL",
                unit: "",
            },
        ])
    }

    const removeDataset = () => {
        const updatedDatasets = [...datasetsToCreate];
        updatedDatasets.pop();
        setDatasetsToCreate(updatedDatasets);
    }

    const titleBodyTemplate = (options) => {
        const currentTitle = datasetsToCreate[options.id - 1].title;
        return (
            <InputText
                type="text"
                className="w-full"
                value={currentTitle}
                onChange={(e) => inputChangeHandler(options, e, "title")}
            />
        );
    };

    const dataTypeBodyTemplate = (options) => {
        const currentDataType = datasetsToCreate[options.id - 1].dataType;
        return (
            <Dropdown
                className="w-full"
                options={DATASET_DATA_TYPES}
                value={currentDataType}
                onChange={(e) => inputChangeHandler(options, e, "dataType")}
            />
        );
    };

    const accessTypeBodyTemplate = (options) => {
        const currentAccessType = datasetsToCreate[options.id - 1].accessType;
        return (
            <Dropdown
                className="w-full"
                options={DATASET_ACCESS_TYPES}
                value={currentAccessType}
                onChange={(e) => inputChangeHandler(options, e, "accessType")}
            />
        );
    };

    const inputChangeHandler = (options, e, field) => {
        setDatasetsToCreate((prevState) => {
            let updatedDatasets = [...prevState];
            updatedDatasets[options.id - 1][field] = e.target ? e.target.value : e.value;
            return updatedDatasets;
        });
    };

    const unitBodyTemplate = (options) => {
        const unitSelectorValue = datasetsToCreate[options.id - 1].unit;
        return (
            <DatasetUnitSelector
                className="w-full"
                value={unitSelectorValue}
                onChange={(e) => unitChangeHandler(options, e)}
            />
        );
    };

    const unitChangeHandler = (options, e) => {
        setDatasetsToCreate((prevState) => {
            let updatedDatasets = [...prevState];
            updatedDatasets[options.id - 1].unit = e;
            return updatedDatasets;
        });
    };

    const {mutateAsync} = useCreateCategorizedDataset()

    async function createDatasetsHandler() {
        try {

            const emptyTitleIndex = datasetsToCreate.findIndex((dataset) => dataset.title.trim() === '');

            if (emptyTitleIndex !== -1) {
                easasAlert('Validation Error', 'Title cannot be empty', 'error');
                return;
            }

            if (datasetsToCreate.length === 0) {
                easasAlert('Validation Error', 'No datasets to create', 'error');
                return;
            }

            const results = await Promise.allSettled(
                datasetsToCreate.map(async (dataset) => {
                    const data: Partial<BaseDatasetDTO> = {
                        title: dataset.title,
                        categoryId: defaultCategory,
                        dataType: dataset.dataType,
                        accessType: dataset.accessType,
                        unit: dataset.unit,
                    };

                    return await mutateAsync(data);
                })
            );

            const successfulMutations = results.filter((result) => result.status === 'fulfilled');
            const failedMutations = results.filter((result) => result.status === 'rejected');


            if (successfulMutations.length === results.length) {
                const createdDatasets = successfulMutations.map((result) => {
                    if (result.status === 'fulfilled') {
                        return result.value
                    }
                });
                setCreatedDatasets(createdDatasets);
                easasAlert('DATASETS CREATED', `All datasets have been created`, 'success');
            } else {
                easasAlert('CREATION FAILED', `${failedMutations.length} datasets failed to create`, 'error');
            }
        } catch (error) {
            console.error('Error creating datasets:', error);
            easasAlert('ERROR', 'An error occurred while creating datasets', 'error');
        }
    }

    return (<>
            {createdDatasets.length > 0 ? (<div className="flex col-12 align-items-center">
                    <DataTable value={createdDatasets} className="w-full">
                        <Column
                            field="title"
                            header="Title"
                            body={(e) => (
                                <CopyTextWithButton text={e.title} copyableText={e.title}/>
                            )}
                        />
                        <Column
                            field="identifier"
                            header="Identifier"
                            body={(e) => (
                                <CopyTextWithButton
                                    text={e.identifier}
                                    copyableText={e.identifier}
                                />
                            )}
                        />
                    </DataTable>
                </div>) :
                (<>
                    <DataTable value={datasetsToCreate} header={header} className="p-datatable-gridlines w-full mb-5">
                        <Column
                            field="title"
                            header="Title"
                            style={{width: "55%", padding: 0}}
                            body={titleBodyTemplate}/>
                        <Column
                            field="dataType"
                            header="Type"
                            style={{width: "15%", padding: 0}}
                            body={dataTypeBodyTemplate}/>
                        <Column
                            field="accessType"
                            header="Access"
                            style={{width: "15%", padding: 0}}
                            body={accessTypeBodyTemplate}
                        />
                        <Column
                            field="unit"
                            header="Unit"
                            style={{width: "15%", padding: 0}}
                            body={unitBodyTemplate}/>
                    </DataTable>
                    <div className="flex col-12 gap-2">
                        <Button className="p-button-success" label="Create" onClick={createDatasetsHandler}/>
                        <Button className="p-button-danger" label="Cancel"
                                onClick={() => setCreateDialogVisibility(false)}/>
                    </div>
                </>)
            }
        </>
    );
};

export default CreateMultipleDatasets;
